import { spacing, typography } from "../../app/design-system";
/**
 * Gemeinsame Formularfelder für Ruhezeit: Kosten & Berechnung, Vertragsoptionen, Zugriff & Einschränkungen.
 * Wird in Einstellungen (RestPeriodTemplatesPage), im Ruhezeit-Drawer (Personen/Aufgaben) und ggf. anderswo genutzt.
 */
import type { FormInstance } from "../../../../../../shared/frontend/ui";
import { Checkbox, Form, InputNumber, Radio, Select, Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import { CYCLE_UNIT_CONTRACT_PERIOD,CYCLE_UNIT_DAILY,CYCLE_UNIT_MONTHLY,CYCLE_UNIT_ONE_TIME,CYCLE_UNIT_WEEKLY,CYCLE_UNIT_YEARLY } from "../../app/constants/cycleUnit";
import {
  REST_PERIOD_BILLING_SCHEDULE_KEEP_DUE_DATES,
  REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
  REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES,
} from "../../app/constants/restPeriodBillingScheduleMode";
import { useI18n } from "../../app/i18n/useI18n";
import type { UnitsSettings } from "../../app/units/units";
import { CheckmarkChoiceCard } from "../CheckmarkChoiceCard";
import { FormSectionHeading } from "../FormSectionHeading";
import { ResponsiveFormGrid } from "../ui/antd";
import { restPeriodFeeCycleUnitForAmountUnit } from "./restPeriodFeeUtils";
import { REST_BILLING_APPEND_FREE_TO_END, REST_DURATION_RULE_MODE, REST_FEE_MODE } from "./restPeriodFormModes";

export type RestPeriodFormFieldsMode = "template" | "instance";

export type RestPeriodFormFieldsProps = {
  form: FormInstance;
  mode: RestPeriodFormFieldsMode;
  /** Währung, Dezimaltrenner, Platzhalter (z. B. aus loadUnits/format). */
  currencyCode: string;
  decimalSeparator: string;
  moneyPlaceholder: string;
  units: UnitsSettings;
  /**
   * Erster Abschnitt der gemeinsamen Felder. Im Vorlagen-Drawer stehen davor nur Name und
   * Studio — dort `true`. Hinter einem anderen Abschnitt (Ruhezeit-Drawer) `false`.
   */
  first?: boolean;
  /** Wenn true: Gebühren, Berechnung und Vertrags-/Zugriffsoptionen nur Anzeige (z. B. ohne restPeriods.customize). */
  modelFieldsDisabled?: boolean;
};

type CheckboxOptionProps = {
  name: string;
  title: React.ReactNode;
  description: React.ReactNode;
  disabled?: boolean;
};

function CheckboxOption({ name, title, description, disabled = false }: CheckboxOptionProps) {
  return (
    <div style={{ minWidth: 0 }}>
      <Form.Item name={name} valuePropName="checked" style={{ marginBottom: 0 }}>
        <Checkbox disabled={disabled}>{title}</Checkbox>
      </Form.Item>
      <Typography.Text type="secondary" style={{ display: "block", fontSize: typography.fontSize.sm, paddingLeft: spacing.xl }}>
        {description}
      </Typography.Text>
    </div>
  );
}

export function RestPeriodFormFields({
  form,
  mode,
  currencyCode,
  decimalSeparator,
  moneyPlaceholder,
  first = true,
  modelFieldsDisabled = false,
}: RestPeriodFormFieldsProps) {
  const { text } = useI18n();
  const watchedMonthlyFee = Form.useWatch("monthlyFee", form);
  const watchedWeeksCount = Form.useWatch("weeksCount", form);
  const selectedFeeMode = Form.useWatch("restPeriodFeeMode", form);
  const selectedDurationRuleMode = Form.useWatch("restPeriodDurationRuleMode", form);
  const billingScheduleMode = Form.useWatch("billingScheduleMode", form);
  const extendsContract = Form.useWatch("extendsContract", form);
  const continuePayment = Form.useWatch("continuePayment", form);
  const appendFreeToEnd = Form.useWatch("appendFreeToEnd", form);
  const requireFullPeriods = Form.useWatch("requireFullPeriods", form);
  const minimumRestPeriodValue = Form.useWatch("minimumRestPeriodValue", form);
  const minimumRestPeriodUnit = Form.useWatch("minimumRestPeriodUnit", form);
  const showFeeDueField = watchedMonthlyFee != null && Number(watchedMonthlyFee) > 0;
  const hasWeeksCount = watchedWeeksCount != null && Number(watchedWeeksCount) > 0;
  const derivedFeeMode = showFeeDueField ? REST_FEE_MODE.Amount : hasWeeksCount ? REST_FEE_MODE.Contributions : REST_FEE_MODE.None;
  const feeMode = selectedFeeMode || derivedFeeMode;
  const hasMinimumRestPeriod = minimumRestPeriodValue != null && Number(minimumRestPeriodValue) > 0 && minimumRestPeriodUnit != null;
  const derivedDurationRuleMode = requireFullPeriods ? REST_DURATION_RULE_MODE.FullPeriods : hasMinimumRestPeriod ? REST_DURATION_RULE_MODE.MinimumDuration : REST_DURATION_RULE_MODE.None;
  const durationRuleMode = selectedDurationRuleMode || derivedDurationRuleMode;
  const recoverTime = billingScheduleMode === REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME;

  // Hinweis: Das Leeren der „anderen“ Modus-Felder (feeAmountUnit/weeksCount) erfolgt
  // ausschließlich in `selectFeeMode` beim tatsächlichen Radio-Wechsel — NICHT in einem
  // useEffect über `feeMode`. Form.useWatch liefert beim ersten Render undefined, wodurch
  // `feeMode` kurzzeitig auf "none" fällt und ein solcher Effekt das gerade geladene
  // feeAmountUnit fälschlich verwerfen würde. `save()` sendet feeAmountUnit ohnehin nur
  // bei gesetztem Betrag (hasRestPeriodFee).

  // Hinweis: Die Radio-Modi (restPeriodFeeMode / restPeriodDurationRuleMode) werden
  // von allen aufrufenden Stellen beim Laden/Öffnen explizit gesetzt. Ein
  // „Init aus abgeleitetem Wert“-Effekt darf hier NICHT laufen: Form.useWatch
  // liefert beim ersten Render noch undefined, wodurch ein solcher Effekt die
  // gerade geladene Auswahl fälschlich auf "none" zurücksetzen würde.
  // Für die Anzeige genügt der Fallback `selectedFeeMode || derivedFeeMode` unten.

  React.useEffect(() => {
    if (recoverTime && !modelFieldsDisabled) {
      form.setFieldsValue({
        extendsContract: true,
        continuePayment: false,
        appendFreeToEnd: false,
      });
    }
  }, [form, modelFieldsDisabled, recoverTime]);

  React.useEffect(() => {
    if (appendFreeToEnd && !modelFieldsDisabled) {
      form.setFieldsValue({
        extendsContract: true,
        continuePayment: true,
        billingScheduleMode: REST_PERIOD_BILLING_SCHEDULE_KEEP_DUE_DATES,
      });
    }
  }, [appendFreeToEnd, form, modelFieldsDisabled]);

  const selectFeeMode = React.useCallback(
    (nextMode: string) => {
      if (nextMode === REST_FEE_MODE.None) {
        form.setFieldsValue({
          restPeriodFeeMode: REST_FEE_MODE.None,
          monthlyFee: null,
          feeAmountUnit: undefined,
          feeCycleUnit: undefined,
          feeCycleValue: null,
          proportionalCalculationMethod: null,
          weeksCount: null,
        });
        return;
      }
      if (nextMode === REST_FEE_MODE.Amount) {
        form.setFieldsValue({
          restPeriodFeeMode: REST_FEE_MODE.Amount,
          feeAmountUnit: showFeeDueField ? form.getFieldValue("feeAmountUnit") : CYCLE_UNIT_MONTHLY,
          feeCycleUnit: showFeeDueField ? form.getFieldValue("feeCycleUnit") : CYCLE_UNIT_MONTHLY,
          feeCycleValue: null,
          proportionalCalculationMethod: null,
          weeksCount: null,
        });
        return;
      }
      form.setFieldsValue({
        restPeriodFeeMode: REST_FEE_MODE.Contributions,
        monthlyFee: null,
        feeAmountUnit: undefined,
        feeCycleUnit: undefined,
        feeCycleValue: null,
        proportionalCalculationMethod: null,
      });
    },
    [form, showFeeDueField]
  );

  const selectDurationRuleMode = React.useCallback(
    (nextMode: string) => {
      if (nextMode === REST_DURATION_RULE_MODE.None) {
        form.setFieldsValue({
          restPeriodDurationRuleMode: REST_DURATION_RULE_MODE.None,
          requireFullPeriods: false,
          minimumRestPeriodValue: null,
          minimumRestPeriodUnit: null,
        });
        return;
      }
      if (nextMode === REST_DURATION_RULE_MODE.MinimumDuration) {
        form.setFieldsValue({
          restPeriodDurationRuleMode: REST_DURATION_RULE_MODE.MinimumDuration,
          requireFullPeriods: false,
        });
        return;
      }
      form.setFieldsValue({
        restPeriodDurationRuleMode: REST_DURATION_RULE_MODE.FullPeriods,
        requireFullPeriods: true,
        minimumRestPeriodValue: null,
        minimumRestPeriodUnit: null,
      });
    },
    [form]
  );

  const billingModelOptions = React.useMemo(
    () => [
      {
        value: REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
        title: text("Recover rest period time"),
        description: text("Rest period billing model recover time description")
      },
      {
        value: REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES,
        title: text("Shift contribution due dates"),
        description: text("Rest period billing model shift description")
      },
      {
        value: REST_PERIOD_BILLING_SCHEDULE_KEEP_DUE_DATES,
        title: text("Correct contributions"),
        description: extendsContract && !continuePayment
          ? text("Rest period billing model keep extending description")
          : text("Rest period billing model keep description")
      },
      {
        value: REST_BILLING_APPEND_FREE_TO_END,
        title: text("Append to end"),
        description: text("Append to end tooltip")
      }
    ],
    [continuePayment, extendsContract, text]
  );

  return (
    <>
      <Form.Item name="feeCycleUnit" hidden><input /></Form.Item>
      <Form.Item name="feeCycleValue" hidden><input /></Form.Item>
      <Form.Item name="proportionalCalculationMethod" hidden><input /></Form.Item>
      <Form.Item name="billingScheduleMode" hidden>
        <input />
      </Form.Item>
      <Form.Item name="extendsContract" hidden valuePropName="checked">
        <input type="checkbox" />
      </Form.Item>
      <Form.Item name="continuePayment" hidden valuePropName="checked">
        <input type="checkbox" />
      </Form.Item>
      <Form.Item name="appendFreeToEnd" hidden valuePropName="checked">
        <input type="checkbox" />
      </Form.Item>

      <FormSectionHeading
        title={text("Rest period balance options")}
        first={first}
        sectionKey="rest-periods.balance"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: spacing.sm
          }}
        >
          {billingModelOptions.map((option) => (
            <CheckmarkChoiceCard
              key={String(option.value)}
              title={option.title}
              description={option.description}
              selected={
                option.value === REST_BILLING_APPEND_FREE_TO_END
                  ? appendFreeToEnd === true
                  : appendFreeToEnd !== true && billingScheduleMode === option.value
              }
              disabled={modelFieldsDisabled}
              onSelect={() => {
                if (option.value === REST_BILLING_APPEND_FREE_TO_END) {
                  form.setFieldsValue({
                    extendsContract: true,
                    continuePayment: true,
                    appendFreeToEnd: true,
                    billingScheduleMode: REST_PERIOD_BILLING_SCHEDULE_KEEP_DUE_DATES,
                  });
                  return;
                }

                form.setFieldsValue({
                  extendsContract: option.value === REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME
                    || option.value === REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES,
                  continuePayment: false,
                  appendFreeToEnd: false,
                  billingScheduleMode: option.value,
                });
              }}
            />
          ))}
        </div>
      </FormSectionHeading>

      <FormSectionHeading title={text("Costs & Calculation")} sectionKey="rest-periods.costs">
        <Form.Item
          name="restPeriodFeeMode"
          label={text("Rest period fee mode")}
          style={{ marginBottom: spacing.sm }}
          getValueFromEvent={(event) => {
            const nextMode = String(event?.target?.value || "none");
            selectFeeMode(nextMode);
            return nextMode;
          }}
        >
          <Radio.Group
            disabled={modelFieldsDisabled}
          >
            <Radio value="none">{text("Rest period fee mode none")}</Radio>
            <Radio value="amount">{text("Rest period fee mode amount")}</Radio>
            <Radio value="contributions">{text("Rest period fee mode contributions")}</Radio>
          </Radio.Group>
        </Form.Item>

        {feeMode === REST_FEE_MODE.Amount ? (
          <ResponsiveFormGrid columns={2}>
            <Form.Item
              name="monthlyFee"
              label={text("Rest period fee")}
              tooltip={text("Rest period fee per cycle tooltip")}
              style={{ marginBottom: 0 }}
              rules={[
                {
                  validator: (_, value) =>
                    value != null && Number(value) > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error(text("Rest period fee amount required"))),
                },
              ]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                decimalSeparator={decimalSeparator}
                addonAfter={currencyCode}
                placeholder={moneyPlaceholder}
                disabled={modelFieldsDisabled}
              />
            </Form.Item>
            <Form.Item
              name="feeAmountUnit"
              label={text("Rest period fee unit")}
              tooltip={text("Rest period fee unit tooltip")}
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: text("Rest period fee unit required") }]}
            >
              <Select
                style={{ width: "100%" }}
                placeholder={text("per month")}
                disabled={modelFieldsDisabled}
                onChange={(unit) => form.setFieldsValue({
                  feeCycleUnit: restPeriodFeeCycleUnitForAmountUnit(unit),
                  feeCycleValue: null,
                  proportionalCalculationMethod: null,
                })}
                options={[
                  { value: CYCLE_UNIT_ONE_TIME, label: text("One-time (whole rest period)") },
                  { value: CYCLE_UNIT_MONTHLY, label: text("per month") },
                  { value: CYCLE_UNIT_DAILY, label: text("per day") },
                  { value: CYCLE_UNIT_WEEKLY, label: text("per week") },
                  { value: CYCLE_UNIT_YEARLY, label: text("per year") },
                  { value: CYCLE_UNIT_CONTRACT_PERIOD, label: text("per contribution period") },
                ]}
              />
            </Form.Item>
          </ResponsiveFormGrid>
        ) : null}

        {feeMode === REST_FEE_MODE.Contributions ? (
          <ResponsiveFormGrid columns={1} minDesktopColumnWidth={180}>
            <Form.Item
              name="weeksCount"
              label={text("Rest period number of weekly contributions")}
              tooltip={text("Rest period number of weekly contributions tooltip")}
              style={{ maxWidth: 280, marginBottom: 0 }}
              rules={[
                {
                  validator: (_, value) =>
                    value != null && Number(value) > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error(text("Rest period contributions required"))),
                },
              ]}
            >
              <InputNumber min={1} step={1} style={{ width: "100%" }} disabled={modelFieldsDisabled} />
            </Form.Item>
          </ResponsiveFormGrid>
        ) : null}
      </FormSectionHeading>

      {mode === "template" ? (
        <FormSectionHeading
          title={text("Rest period duration restrictions")}
          sectionKey="rest-periods.duration"
        >
          <Form.Item
            name="restPeriodDurationRuleMode"
            label={text("Rest period duration rule")}
            style={{ marginBottom: spacing.sm }}
            getValueFromEvent={(event) => {
              const nextMode = String(event?.target?.value || "none");
              selectDurationRuleMode(nextMode);
              return nextMode;
            }}
          >
            <Radio.Group
              disabled={modelFieldsDisabled}
            >
              <Radio value="none">{text("Rest period duration rule none")}</Radio>
              <Radio value="minimumDuration">{text("Rest period duration rule minimum")}</Radio>
              <Radio value="fullPeriods">{text("Rest period duration rule full periods")}</Radio>
            </Radio.Group>
          </Form.Item>

          {durationRuleMode === REST_DURATION_RULE_MODE.MinimumDuration ? (
            <ResponsiveFormGrid columns={2}>
              <Form.Item
                name="minimumRestPeriodValue"
                label={text("Minimum rest period (value)")}
                tooltip={text("Minimum rest period value tooltip")}
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    validator: (_, value) =>
                      value != null && Number(value) > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error(text("Minimum rest period value required"))),
                  },
                ]}
              >
                <InputNumber min={1} step={1} style={{ width: "100%" }} disabled={modelFieldsDisabled} />
              </Form.Item>
              <Form.Item
                name="minimumRestPeriodUnit"
                label={text("Minimum rest period (unit)")}
                tooltip={text("Minimum rest period unit tooltip")}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: text("Minimum rest period unit required") }]}
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder={text("None")}
                  allowClear
                  disabled={modelFieldsDisabled}
                  options={[
                    { value: 1, label: text("Days") },
                    { value: 2, label: text("Weeks") },
                    { value: 3, label: text("Months") },
                    { value: 4, label: text("Years") },
                  ]}
                />
              </Form.Item>
            </ResponsiveFormGrid>
          ) : null}

          <Form.Item name="requireFullPeriods" hidden valuePropName="checked">
            <input type="checkbox" />
          </Form.Item>
        </FormSectionHeading>
      ) : null}

      <FormSectionHeading title={text("Access")} sectionKey="rest-periods.access">
        <ResponsiveFormGrid columns={1}>
          <CheckboxOption
            name="allowCheckInDuringRestPeriod"
            title={text("Check-in allowed")}
            description={text("Check-in allowed tooltip")}
            disabled={modelFieldsDisabled}
          />
        </ResponsiveFormGrid>
      </FormSectionHeading>
    </>
  );
}
